import React, { createContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Création du contexte avec une valeur par défaut
const AppContext = createContext({
  theme: 'light',
  language: 'fr',
  toggleTheme: () => {},
  toggleLanguage: () => {},
  isRTL: false,
});

const AppProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [theme, setTheme] = useState('light');
  const [language, setLanguage] = useState(localStorage.getItem('i18nextLng') || 'fr');

  // Effet pour changer la direction du document selon la langue
  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.body.style.fontFamily = language === 'ar' ? 'Tajawal, sans-serif' : 'Poppins, sans-serif';
    i18n.changeLanguage(language);
    localStorage.setItem('i18nextLng', language);

    // Mettre à jour les meta tags pour le SEO
    const updateMetaTag = (name, content) => {
      let element = document.querySelector(`meta[name="${name}"]`);
      if (element) {
        element.setAttribute('content', content);
      }
    };

    const updateMetaProperty = (property, content) => {
      let element = document.querySelector(`meta[property="${property}"]`);
      if (element) {
        element.setAttribute('content', content);
      }
    };

    if (language === 'ar') {
      // Mettre à jour le titre
      document.title = 'المنظومة الوطنية للفرصة الجديدة - برنامج تعزيز قابلية التشغيل وتمكين الشباب التونسي';
      
      // Mettre à jour les meta tags
      updateMetaTag('description', 'المنظومة الوطنية للفرصة الجديدة توفر فرصة ثانية للشباب المنقطعين عن الدراسة في تونس. برنامج تعزيز قابلية التشغيل وتمكين الشباب التونسي');
      updateMetaProperty('og:title', 'المنظومة الوطنية للفرصة الجديدة - تونس');
      updateMetaProperty('og:description', 'برنامج تعزيز قابلية التشغيل وتمكين الشباب التونسي');
      updateMetaProperty('og:locale', 'ar_TN');
    } else {
      // Mettre à jour le titre en français
      document.title = 'Dispositif National Nouvelle Chance - Programme de renforcement de l\'employabilité et autonomiser les jeunes tunisiens NEET';
      
      // Mettre à jour les meta tags
      updateMetaTag('description', 'Le Dispositif National Nouvelle Chance offre une seconde opportunité aux jeunes déscolarisés en Tunisie. Programme de renforcement de l\'employabilité et autonomiser les jeunes tunisiens NEET');
      updateMetaProperty('og:title', 'Dispositif National Nouvelle Chance - DNNC Tunisie');
      updateMetaProperty('og:description', 'Programme de renforcement de l\'employabilité et autonomiser les jeunes tunisiens NEET');
      updateMetaProperty('og:locale', 'fr_FR');
    }
  }, [language, i18n]);

  // Effet pour appliquer le thème
  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  // Fonction pour changer la langue
  const toggleLanguage = () => {
    setLanguage(prevLang => prevLang === 'fr' ? 'ar' : 'fr');
  };

  // Fonction pour changer le thème
  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  const value = {
    theme,
    setTheme,
    toggleTheme,
    language,
    setLanguage,
    toggleLanguage,
    isRTL: language === 'ar',
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export { AppContext, AppProvider };
